import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  Row,
  Col,
  Typography,
  Form,
  Input,
  Button,
  Radio,
  Alert,
  Card,
  Spin,
  Space,
  Divider,
  Tag,
} from 'antd';
import {
  SettingOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { DashboardLayout } from '../components/layout';
import { getSettings, updateSettings, testLLMConnection } from '../services/api/settingsApi';
import PsaStatusMappingCard from '../features/agents/components/scoping/PsaStatusMappingCard';
import ScheduledJobsPanel from '../features/settings/ScheduledJobsPanel';

const { Title, Text, Paragraph } = Typography;

// =========================================
// Styled Components
// =========================================

const PageContainer = styled.div`
  padding: 24px;
  max-width: 860px;
`;

const SectionCard = styled(Card)`
  margin-bottom: 24px;
`;

const ReadOnlyCard = styled(SectionCard)`
  opacity: 0.85;
`;

// Scheduled Jobs is a wide data grid, not a stacked form. PageContainer caps
// content at 860px — right for everything else on this page, but ~210px too
// narrow for this table, which used to overflow its card and paint the State
// column and every Run-now button outside the card border. PageContainer is
// left-aligned, so only its right edge constrains us: grow into the unused
// main-area space, never past the viewport, never below the column's own width.
const WideSectionCard = styled(SectionCard)`
  width: clamp(100%, calc(100vw - 340px), 1220px);
  max-width: none;
`;

const ProviderLabel = styled.span`
  font-size: 14px;
  color: var(--odin-text-primary);
`;

const InfoNote = styled(Paragraph)`
  color: var(--odin-text-tertiary) !important;
  font-size: 13px !important;
  margin-top: 8px !important;
  margin-bottom: 0 !important;
`;

/**
 * Admin-only Settings page for configuring LLM provider at runtime.
 * Allows switching between Local (Ollama) and Azure AI Foundry without restart.
 */
function SettingsPage() {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [provider, setProvider] = useState('ollama');
  const [apiKeyChanged, setApiKeyChanged] = useState(false);
  const [foundryKeyChanged, setFoundryKeyChanged] = useState(false);
  const [saveResult, setSaveResult] = useState(null); // { type: 'success'|'error', message: string }
  const [testResult, setTestResult] = useState(null);
  const [embeddingInfo, setEmbeddingInfo] = useState(null);

  // Load current settings from agent-backend on mount
  const loadSettings = useCallback(async () => {
    setLoading(true);
    setSaveResult(null);
    setTestResult(null);
    try {
      const data = await getSettings();
      setProvider(data.llm_provider || 'ollama');
      form.setFieldsValue({
        llm_provider: data.llm_provider || 'ollama',
        azure_endpoint: data.azure?.endpoint || '',
        azure_deployment: data.azure?.deployment || 'gpt-4o',
        azure_api_version: data.azure?.api_version || '2024-08-01-preview',
        ollama_base_url: data.ollama?.base_url || 'http://host.docker.internal:11434/v1',
        ollama_model: data.ollama?.model || 'qwen2.5-32k',
        foundry_endpoint: data.foundry?.endpoint || '',
        foundry_model: data.foundry?.model || 'claude-sonnet-4-6',
      });
      setEmbeddingInfo(data.embedding || null);
    } catch (err) {
      setSaveResult({ type: 'error', message: `Failed to load settings: ${err.message}` });
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleProviderChange = (e) => {
    setProvider(e.target.value);
    setSaveResult(null);
    setTestResult(null);
  };

  const handleApiKeyChange = () => {
    setApiKeyChanged(true);
    setSaveResult(null);
  };

  const handleFoundryKeyChange = () => {
    setFoundryKeyChanged(true);
    setSaveResult(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);
    setTestResult(null);
    try {
      const values = form.getFieldsValue();
      const payload = {
        llm_provider: values.llm_provider,
        ollama: {
          base_url: values.ollama_base_url,
          model: values.ollama_model,
        },
        azure: {
          endpoint: values.azure_endpoint,
          deployment: values.azure_deployment,
          api_version: values.azure_api_version,
          // Only send api_key if the user explicitly entered a new one
          ...(apiKeyChanged && values.azure_api_key
            ? { api_key: values.azure_api_key }
            : {}),
        },
        foundry: {
          endpoint: values.foundry_endpoint || '',
          model: values.foundry_model || 'claude-sonnet-4-6',
          ...(foundryKeyChanged && values.foundry_api_key
            ? { api_key: values.foundry_api_key }
            : {}),
        },
      };
      await updateSettings(payload);
      setApiKeyChanged(false);
      setFoundryKeyChanged(false);
      form.setFieldValue('azure_api_key', '');
      form.setFieldValue('foundry_api_key', '');
      setSaveResult({ type: 'success', message: 'Settings saved. New provider active within 30 seconds.' });
    } catch (err) {
      setSaveResult({ type: 'error', message: `Save failed: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const values = form.getFieldsValue();
      const testPayload = {
        provider: values.llm_provider,
        azure: {
          endpoint: values.azure_endpoint,
          deployment: values.azure_deployment,
          api_version: values.azure_api_version,
          // Include new key if entered, otherwise backend uses stored key
          ...(apiKeyChanged && values.azure_api_key
            ? { api_key: values.azure_api_key }
            : {}),
        },
        ollama: {
          base_url: values.ollama_base_url,
          model: values.ollama_model,
        },
        foundry: {
          endpoint: values.foundry_endpoint || '',
          model: values.foundry_model || 'claude-sonnet-4-6',
          ...(foundryKeyChanged && values.foundry_api_key
            ? { api_key: values.foundry_api_key }
            : {}),
        },
      };
      const result = await testLLMConnection(testPayload);
      if (result.status === 'ok') {
        const latency = result.latency_seconds;
        const detail = result.deployment || result.model || '';
        setTestResult({
          type: 'success',
          message: `Connected successfully${detail ? ` to ${detail}` : ''} in ${latency}s. Response: "${result.response}"`,
        });
      } else {
        setTestResult({
          type: 'error',
          message: `Connection failed: ${result.message || result.error}`,
        });
      }
    } catch (err) {
      setTestResult({ type: 'error', message: `Test failed: ${err.message}` });
    } finally {
      setTesting(false);
    }
  };

  return (
    <DashboardLayout>
      <PageContainer>
        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 8 }}>
          <SettingOutlined style={{ marginRight: 10 }} />
          ODIN1 Settings
        </Title>
        <Text style={{ color: 'var(--odin-text-tertiary)', fontSize: 13 }}>
          Admin-only — changes take effect within 30 seconds without container restart.
        </Text>

        <Divider style={{ borderColor: 'var(--odin-border)', marginTop: 16 }} />

        {loading ? (
          <Spin size="large" style={{ display: 'block', margin: '60px auto' }} />
        ) : (
          <Form form={form} layout="vertical">
            {/* ── LLM Provider Section ── */}
            <SectionCard
              title={
                <span>
                  <ApiOutlined style={{ marginRight: 8 }} />
                  AI Model Provider (LLM)
                </span>
              }
            >
              {/* Provider toggle */}
              <Form.Item name="llm_provider" label={<Text style={{ color: 'var(--odin-text-primary)' }}>Provider</Text>}>
                <Radio.Group onChange={handleProviderChange} value={provider}>
                  <Radio value="ollama">
                    <ProviderLabel>Local (Ollama)</ProviderLabel>
                  </Radio>
                  <Radio value="azure">
                    <ProviderLabel>Azure AI Foundry</ProviderLabel>
                  </Radio>
                  <Radio value="foundry">
                    <ProviderLabel>Azure AI Foundry (Claude)</ProviderLabel>
                  </Radio>
                </Radio.Group>
              </Form.Item>

              {/* Azure settings — visible when azure selected */}
              {provider === 'azure' && (
                <>
                  <Divider style={{ borderColor: 'var(--odin-border)', margin: '8px 0 20px' }} />
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        name="azure_endpoint"
                        label={<Text style={{ color: 'var(--odin-text-primary)' }}>Endpoint</Text>}
                        rules={[{ required: provider === 'azure', message: 'Endpoint is required for Azure' }]}
                      >
                        <Input placeholder="https://myresource.openai.azure.com" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="azure_deployment"
                        label={<Text style={{ color: 'var(--odin-text-primary)' }}>Deployment Name</Text>}
                      >
                        <Input placeholder="gpt-4o" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="azure_api_version"
                        label={<Text style={{ color: 'var(--odin-text-primary)' }}>API Version</Text>}
                      >
                        <Input placeholder="2024-08-01-preview" />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="azure_api_key"
                        label={<Text style={{ color: 'var(--odin-text-primary)' }}>API Key</Text>}
                      >
                        <Input.Password
                          placeholder="Leave blank to keep current stored key"
                          onChange={handleApiKeyChange}
                        />
                      </Form.Item>
                      <InfoNote>
                        <InfoCircleOutlined style={{ marginRight: 6 }} />
                        Leave the API key blank to keep the currently stored key. Only enter a new value to change it.
                      </InfoNote>
                    </Col>
                  </Row>
                </>
              )}

              {/* Foundry settings — visible when foundry selected */}
              {provider === 'foundry' && (
                <>
                  <Divider style={{ borderColor: 'var(--odin-border)', margin: '8px 0 20px' }} />
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        name="foundry_endpoint"
                        label={<Text style={{ color: 'var(--odin-text-primary)' }}>Endpoint</Text>}
                      >
                        <Input placeholder="https://<resource>.services.ai.azure.com/anthropic/v1/messages" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="foundry_model"
                        label={<Text style={{ color: 'var(--odin-text-primary)' }}>Deployment Name</Text>}
                      >
                        <Input placeholder="claude-sonnet-4-6" />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="foundry_api_key"
                        label={<Text style={{ color: 'var(--odin-text-primary)' }}>API Key</Text>}
                      >
                        <Input.Password
                          placeholder="Leave blank to keep current stored key"
                          onChange={handleFoundryKeyChange}
                        />
                      </Form.Item>
                      <InfoNote>
                        <InfoCircleOutlined style={{ marginRight: 6 }} />
                        Single resource key shared across all Claude deployments. Deployment name (e.g.{' '}
                        <code style={{ color: 'var(--odin-accent)' }}>claude-sonnet-4-6</code>) is sent as the{' '}
                        <code style={{ color: 'var(--odin-accent)' }}>model</code> field in each request.
                      </InfoNote>
                    </Col>
                  </Row>
                </>
              )}

              {/* Ollama settings — visible when ollama selected */}
              {provider === 'ollama' && (
                <>
                  <Divider style={{ borderColor: 'var(--odin-border)', margin: '8px 0 20px' }} />
                  <Row gutter={16}>
                    <Col span={16}>
                      <Form.Item
                        name="ollama_base_url"
                        label={<Text style={{ color: 'var(--odin-text-primary)' }}>Ollama Base URL</Text>}
                      >
                        <Input placeholder="http://host.docker.internal:11434/v1" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="ollama_model"
                        label={<Text style={{ color: 'var(--odin-text-primary)' }}>Model</Text>}
                      >
                        <Input placeholder="qwen2.5-32k" />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

              {/* Feedback messages */}
              {saveResult && (
                <Alert
                  type={saveResult.type}
                  message={saveResult.message}
                  showIcon
                  style={{ marginBottom: 16 }}
                  closable
                  onClose={() => setSaveResult(null)}
                />
              )}
              {testResult && (
                <Alert
                  type={testResult.type}
                  message={testResult.message}
                  showIcon
                  style={{ marginBottom: 16 }}
                  closable
                  onClose={() => setTestResult(null)}
                />
              )}

              {/* Action buttons */}
              <Space>
                <Button
                  icon={<ApiOutlined />}
                  onClick={handleTestConnection}
                  loading={testing}
                  disabled={saving}
                >
                  Test Connection
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={saving}
                  disabled={testing}
                >
                  Save Settings
                </Button>
              </Space>
            </SectionCard>

            {/* ── Audit Request — PSA status mapping (Task 6) ── */}
            <PsaStatusMappingCard />

            {/* ── Scheduled Jobs registry (Task 7) ── */}
            <WideSectionCard
              title={
                <span>
                  <ClockCircleOutlined style={{ marginRight: 8 }} />
                  Scheduled Jobs
                </span>
              }
            >
              <ScheduledJobsPanel />
            </WideSectionCard>

            {/* ── Embedding Info Section (read-only) ── */}
            <ReadOnlyCard
              title={
                <span>
                  <DatabaseOutlined style={{ marginRight: 8 }} />
                  Knowledge Base Embeddings
                  <Tag color="default" style={{ marginLeft: 12, fontSize: 11 }}>
                    Read-only
                  </Tag>
                </span>
              }
            >
              <Row gutter={24}>
                <Col>
                  <Text style={{ color: 'var(--odin-text-tertiary)', fontSize: 12 }}>PROVIDER</Text>
                  <br />
                  <Text style={{ color: 'var(--odin-text-primary)', fontSize: 14 }}>
                    {embeddingInfo?.provider === 'azure'
                      ? 'Azure AI Foundry'
                      : embeddingInfo?.provider === 'openai'
                      ? 'OpenAI'
                      : 'Local (Ollama)'}
                  </Text>
                </Col>
                <Col>
                  <Text style={{ color: 'var(--odin-text-tertiary)', fontSize: 12 }}>MODEL</Text>
                  <br />
                  <Text style={{ color: 'var(--odin-text-primary)', fontSize: 14 }}>
                    {embeddingInfo?.model || 'nomic-embed-text'}
                    <Text style={{ color: 'var(--odin-text-tertiary)', fontSize: 12, marginLeft: 6 }}>
                      ({embeddingInfo?.dimension || 768} dims)
                    </Text>
                  </Text>
                </Col>
                <Col>
                  <Text style={{ color: 'var(--odin-text-tertiary)', fontSize: 12 }}>STATUS</Text>
                  <br />
                  <Text style={{ color: 'var(--odin-success)', fontSize: 14 }}>
                    <CheckCircleOutlined style={{ marginRight: 4 }} />
                    Active
                  </Text>
                </Col>
              </Row>
              <InfoNote style={{ marginTop: 16 }}>
                <InfoCircleOutlined style={{ marginRight: 6 }} />
                Changing the embedding provider requires re-indexing the knowledge base (all chunks must be regenerated).
                Configure via the Knowledge-MCP <code style={{ color: 'var(--odin-accent)' }}>EMBEDDING_PROVIDER</code> environment variable,
                then clear and re-crawl all knowledge sources.
              </InfoNote>
            </ReadOnlyCard>
          </Form>
        )}
      </PageContainer>
    </DashboardLayout>
  );
}

export default SettingsPage;
